import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useEventBusStore } from '../stores/event-bus.store';

export const Route = createFileRoute('/w/$workspaceId/client-invites')({
  component: ClientInvitesManagementPage,
});

const INVITES_PER_PAGE = 10;

/** API pode retornar array direto ou envelope `{ data: [] }` (ex.: paginação); evita `.map is not a function`. */
function unwrapList(payload: unknown): any[] {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: any[] }).data;
  }
  return [];
}

function asArray(payload: unknown): any[] {
  return Array.isArray(payload) ? payload : unwrapList(payload);
}

function inviteStatusLabel(status: string): string {
  switch (status) {
    case 'pending': return 'Pendente';
    case 'accepted': return 'Aceito';
    case 'revoked': return 'Revogado';
    default: return status;
  }
}

function inviteStatusColor(status: string): string {
  switch (status) {
    case 'accepted': return 'bg-green-100 text-green-800';
    case 'revoked': return 'bg-red-100 text-red-700';
    default: return 'bg-yellow-100 text-yellow-800';
  }
}

function ProcessCard({ p }: { p: any }) {
  const title = p.internalTitle || p.parties || 'Processo';
  return (
    <div className="p-3 rounded-[10px] bg-white border border-gray-100">
      <p className="text-sm font-semibold text-[#06141B] truncate">{title}</p>
      <div className="flex flex-wrap items-center gap-2 mt-0.5">
        <p className="text-xs text-gray-600">{p.processNumber}</p>
        {Array.isArray(p.tags) && p.tags.map((tag: any) => (
          <span key={tag.id} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
            {tag.name}
          </span>
        ))}
      </div>
      {p.internalTitle && p.parties && p.internalTitle !== p.parties ? (
        <p className="text-[11px] text-gray-500 mt-0.5 truncate">{p.parties}</p>
      ) : null}
      {Array.isArray(p.parts) && p.parts.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {p.parts.map((part: any) => (
            <span key={part.id} className="text-[11px] text-gray-500">
              {part.name}
              {part.pole ? <span className="text-[10px] text-gray-400 ml-1">({part.pole})</span> : null}
            </span>
          ))}
        </div>
      ) : p.parties ? (
        <p className="text-[11px] text-gray-500 mt-0.5 truncate">{p.parties}</p>
      ) : null}
    </div>
  );
}

function ClientInvitesManagementPage() {
  const { workspaceId } = Route.useParams();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const { confirm } = useConfirmDialog();
  const addToast = useEventBusStore((s) => s.addToast);

  // Create invite form
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedProcessIds, setSelectedProcessIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  // Invite list
  const [invitePage, setInvitePage] = useState(1);

  // Edit invite state
  const [editingInviteId, setEditingInviteId] = useState<string | null>(null);
  const [editClientId, setEditClientId] = useState<string>('');
  const [editProcessIds, setEditProcessIds] = useState<Set<string>>(new Set());

  // View processes for an invite (expand)
  const [expandedInviteId, setExpandedInviteId] = useState<string | null>(null);

  const headers = useMemo(() => ({ 'X-Workspace-Id': workspaceId }), [workspaceId]);

  const { data: clients } = useQuery<any[]>({
    queryKey: ['clients', workspaceId],
    queryFn: async () => {
      const r = await api.get('/clients', { headers });
      return unwrapList(r.data?.data ?? r.data);
    },
    enabled: !!workspaceId,
  });

  // Processes for create form
  const { data: processes, isLoading: isLoadingProcesses } = useQuery<any[]>({
    queryKey: ['client-processes', workspaceId, selectedClientId],
    queryFn: async () => {
      const r = await api.get(`/clients/${selectedClientId}/processes`, { headers });
      return unwrapList(r.data?.data ?? r.data);
    },
    enabled: !!workspaceId && !!selectedClientId,
  });

  // Processes for edit form
  const { data: editProcesses, isLoading: isLoadingEditProcesses } = useQuery<any[]>({
    queryKey: ['client-processes', workspaceId, editClientId],
    queryFn: async () => {
      const r = await api.get(`/clients/${editClientId}/processes`, { headers });
      return unwrapList(r.data?.data ?? r.data);
    },
    enabled: !!workspaceId && !!editClientId,
  });

  const { data: invites, isLoading: isLoadingInvites } = useQuery<any[]>({
    queryKey: ['client-portal', 'invites', workspaceId],
    queryFn: async () => {
      const r = await api.get('/client-portal/invites', { headers });
      return unwrapList(r.data?.data ?? r.data);
    },
    enabled: !!workspaceId,
  });

  /** Convite em modo visualização OU edição (nunca os dois ao mesmo tempo). */
  const inviteProcessesListId = expandedInviteId ?? editingInviteId;
  const { data: inviteProcessesList, isLoading: isLoadingInviteProcessesList } = useQuery<any[]>({
    queryKey: ['client-portal', 'invite-processes', workspaceId, inviteProcessesListId],
    queryFn: async () => {
      const r = await api.get(`/client-portal/invites/${inviteProcessesListId}/processes`, { headers });
      return unwrapList(r.data?.data ?? r.data);
    },
    enabled: !!workspaceId && !!inviteProcessesListId,
  });

  useEffect(() => {
    if (!editingInviteId || editClientId) return;
    const first = (inviteProcessesList || [])[0];
    if (first?.clientId) setEditClientId(first.clientId);
  }, [editingInviteId, editClientId, inviteProcessesList]);

  // Paginated invites (defesa extra se o cache vier em formato envelope)
  const allInvites = asArray(invites);
  const inviteTotalPages = Math.ceil(allInvites.length / INVITES_PER_PAGE) || 1;
  const pagedInvites = allInvites.slice(
    (invitePage - 1) * INVITES_PER_PAGE,
    invitePage * INVITES_PER_PAGE,
  );

  const clientsList = asArray(clients);
  const processesList = asArray(processes);
  const editProcessesList = asArray(editProcesses);
  const inviteProcesses = asArray(inviteProcessesList);

  const createInviteMutation = useMutation({
    mutationFn: async () => {
      setError('');
      const processIds = Array.from(selectedProcessIds);
      const r = await api.post(
        '/client-portal/invite',
        { email, name: name || undefined, processIds },
        { headers },
      );
      return r.data?.data ?? r.data;
    },
    onSuccess: () => {
      setEmail('');
      setName('');
      setSelectedClientId('');
      setSelectedProcessIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['client-portal', 'invites', workspaceId] });
      addToast('Convite enviado com sucesso.', 'success');
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Erro ao criar convite');
    },
  });

  const updateInviteMutation = useMutation({
    mutationFn: async ({ inviteId, processIds }: { inviteId: string; processIds: string[] }) => {
      const r = await api.patch(
        `/client-portal/invites/${inviteId}`,
        { processIds },
        { headers },
      );
      return r.data?.data ?? r.data;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['client-portal', 'invites', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['client-portal', 'invite-processes', workspaceId, inviteId] });
      setEditingInviteId(null);
      setEditClientId('');
      setEditProcessIds(new Set());
      addToast('Convite atualizado.', 'success');
    },
    onError: (err: any) => {
      addToast(err.response?.data?.message || 'Erro ao atualizar convite', 'error');
    },
  });

  const deleteInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      await api.delete(`/client-portal/invites/${inviteId}`, { headers });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-portal', 'invites', workspaceId] });
    },
  });

  const handleDeleteInvite = async (inv: any) => {
    const ok = await confirm({
      title: 'Excluir convite?',
      description: (
        <span>
          O convite para <strong>{inv.email}</strong> será removido e o cliente deixará de ter acesso
          aos processos vinculados a este convite no portal.
        </span>
      ),
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteInviteMutation.mutateAsync(inv.id);
      addToast('Convite excluído.', 'success');
      if (editingInviteId === inv.id) {
        setEditingInviteId(null);
        setEditClientId('');
        setEditProcessIds(new Set());
      }
    } catch (err: any) {
      addToast(err.response?.data?.message || 'Erro ao excluir convite', 'error');
    }
  };

  const openEdit = (inv: any) => {
    setExpandedInviteId(null);
    setEditingInviteId(inv.id);
    setEditClientId('');
    const pids = inv.processIds;
    setEditProcessIds(new Set(Array.isArray(pids) ? pids : []));
  };

  const cancelEdit = () => {
    setEditingInviteId(null);
    setEditClientId('');
    setEditProcessIds(new Set());
  };

  const saveEdit = () => {
    if (!editingInviteId) return;
    updateInviteMutation.mutate({
      inviteId: editingInviteId,
      processIds: Array.from(editProcessIds),
    });
  };

  if (!hasPermission('client-portal.invite')) {
    return (
      <div className="p-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
        <div className="max-w-4xl mx-auto bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-600">Você não tem permissão para convidar clientes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create invite form */}
        <section className="lg:col-span-1 bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
          <h1 className="text-[16px] font-semibold text-[#06141B]">Convite de Cliente</h1>
          <p className="text-[12px] text-gray-500 mt-1">
            Convide um cliente e selecione os processos que ele poderá visualizar.
          </p>

          <div className="mt-5 space-y-4">
            <div>
              <label className="text-[12px] font-medium text-gray-700">Email do cliente</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-[12px] bg-gray-50 border border-gray-200 text-sm"
                placeholder="cliente@email.com"
              />
            </div>

            <div>
              <label className="text-[12px] font-medium text-gray-700">Nome (opcional)</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-[12px] bg-gray-50 border border-gray-200 text-sm"
                placeholder="Nome do cliente"
              />
            </div>

            <div>
              <label className="text-[12px] font-medium text-gray-700">Selecionar cliente</label>
              <select
                value={selectedClientId}
                onChange={(e) => {
                  setSelectedClientId(e.target.value);
                  setSelectedProcessIds(new Set());
                }}
                className="mt-2 w-full px-4 py-3 rounded-[12px] bg-gray-50 border border-gray-200 text-sm"
              >
                <option value="">Selecione...</option>
                {clientsList.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[12px] font-medium text-gray-700">Processos</label>
              <div className="mt-2 max-h-56 overflow-auto rounded-[12px] border border-gray-200 bg-gray-50 p-3 space-y-2">
                {!selectedClientId ? (
                  <p className="text-sm text-gray-500">Selecione um cliente para listar processos.</p>
                ) : isLoadingProcesses ? (
                  <p className="text-sm text-gray-500">Carregando processos...</p>
                ) : !processesList.length ? (
                  <p className="text-sm text-gray-500">Nenhum processo encontrado.</p>
                ) : (
                  processesList.map((p: any) => {
                    const checked = selectedProcessIds.has(p.id);
                    return (
                      <label key={p.id} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedProcessIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(p.id);
                              else next.delete(p.id);
                              return next;
                            });
                          }}
                          className="mt-1"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <p className="text-sm text-gray-800">{p.processNumber}</p>
                            {Array.isArray(p.tags) && p.tags.map((tag: any) => (
                              <span key={tag.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                {tag.name}
                              </span>
                            ))}
                          </div>
                          {Array.isArray(p.parts) && p.parts.length > 0 ? (
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                              {p.parts.map((part: any) => (
                                <span key={part.id} className="text-[10px] text-gray-500">
                                  {part.name}{part.pole ? ` (${part.pole})` : ''}
                                </span>
                              ))}
                            </div>
                          ) : p.parties ? (
                            <p className="text-xs text-gray-500 truncate">{p.parties}</p>
                          ) : null}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Selecionados: {selectedProcessIds.size}
              </p>
            </div>

            {error && (
              <div className="p-3 rounded-[12px] bg-red-50 border border-red-100 text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="button"
              disabled={createInviteMutation.isPending || !email.trim() || selectedProcessIds.size === 0}
              onClick={() => createInviteMutation.mutate()}
              className="w-full px-4 py-3 rounded-[12px] bg-[#06141B] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {createInviteMutation.isPending ? 'Enviando...' : 'Enviar convite'}
            </button>
          </div>
        </section>

        {/* Invites list */}
        <section className="lg:col-span-2 bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
          <h2 className="text-[14px] font-semibold text-[#06141B]">Convites ativos</h2>
          <p className="text-[12px] text-gray-500 mt-1">
            Clique em um convite para editar os processos vinculados. Use excluir para remover o convite e revogar acessos.
          </p>

          {isLoadingInvites ? (
            <p className="text-sm text-gray-500 mt-4">Carregando convites...</p>
          ) : !allInvites.length ? (
            <p className="text-sm text-gray-500 mt-4">Nenhum convite ainda.</p>
          ) : (
            <>
              <div className="mt-4 space-y-2">
                {pagedInvites.map((inv: any) => {
                  const isEditing = editingInviteId === inv.id;
                  const isExpanded = expandedInviteId === inv.id && !isEditing;

                  return (
                    <div key={inv.id} className="rounded-[12px] border border-gray-100 overflow-hidden">
                      <div className="p-4 flex items-start justify-between gap-4">
                        <button
                          type="button"
                          className="min-w-0 text-left flex-1"
                          onClick={() => {
                            if (isEditing) {
                              cancelEdit();
                            } else {
                              openEdit(inv);
                            }
                          }}
                        >
                          <p className="text-sm font-semibold text-[#06141B] truncate">{inv.email}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {Array.isArray(inv.processIds) ? inv.processIds.length : 0} processo(s)
                            {inv.existingUser ? ' • Conta criada' : ' • Sem conta'}
                            <span className="ml-1 text-[#06141B] font-medium">
                              {isEditing ? ' ▲ Editar' : ' ▼ Editar processos'}
                            </span>
                          </p>
                        </button>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-xs px-2.5 py-1 rounded-[10px] whitespace-nowrap font-medium ${inviteStatusColor(inv.status)}`}>
                            {inviteStatusLabel(inv.status)}
                          </span>
                          {!isEditing && (
                            <button
                              type="button"
                              onClick={() => setExpandedInviteId(isExpanded ? null : inv.id)}
                              className="text-xs px-3 py-1.5 rounded-[10px] border border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                              {isExpanded ? 'Ocultar' : 'Ver processos'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDeleteInvite(inv)}
                            disabled={deleteInviteMutation.isPending}
                            className="text-xs px-3 py-1.5 rounded-[10px] border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            Excluir
                          </button>
                        </div>
                      </div>

                      {/* Edit panel */}
                      {isEditing && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50/80">
                          <p className="text-[12px] font-semibold text-[#06141B] pt-3 mb-2">
                            Editar processos do convite
                          </p>
                          <p className="text-[11px] text-gray-500 mb-3">
                            Desmarque para revogar o acesso a um processo no portal do cliente. Salve para aplicar.
                          </p>

                          <div className="mb-3">
                            <p className="text-[11px] font-medium text-gray-700 mb-2">
                              Processos neste convite
                            </p>
                            {isLoadingInviteProcessesList ? (
                              <p className="text-sm text-gray-500 py-2">Carregando processos do convite...</p>
                            ) : !(inviteProcessesList || []).length ? (
                              <p className="text-sm text-gray-500 py-2">
                                Nenhum processo listado. Use a seção abaixo para adicionar por cliente.
                              </p>
                            ) : (
                              <div className="max-h-52 overflow-auto rounded-[10px] border border-gray-200 bg-white p-3 space-y-2">
                                {inviteProcesses.map((p: any) => (
                                  <label key={p.id} className="flex items-start gap-3 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={editProcessIds.has(p.id)}
                                      onChange={(e) => {
                                        setEditProcessIds((prev) => {
                                          const next = new Set(prev);
                                          if (e.target.checked) next.add(p.id);
                                          else next.delete(p.id);
                                          return next;
                                        });
                                      }}
                                      className="mt-1"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-[#06141B] truncate">
                                        {p.internalTitle || p.parties || 'Processo'}
                                      </p>
                                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                        <span className="text-xs text-gray-600">{p.processNumber}</span>
                                        {Array.isArray(p.tags) &&
                                          p.tags.map((tag: any) => (
                                            <span
                                              key={tag.id}
                                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700"
                                            >
                                              {tag.name}
                                            </span>
                                          ))}
                                      </div>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="mb-3">
                            <label className="text-[11px] font-medium text-gray-600">
                              Adicionar mais processos (por cliente)
                            </label>
                            <select
                              value={editClientId}
                              onChange={(e) => setEditClientId(e.target.value)}
                              className="mt-1 w-full px-3 py-2 rounded-[10px] bg-white border border-gray-200 text-sm"
                            >
                              <option value="">Selecione um cliente...</option>
                              {clientsList.map((c: any) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </div>

                          {editClientId && (
                            <div className="mb-3 max-h-56 overflow-auto rounded-[10px] border border-gray-200 bg-white p-3 space-y-2">
                              {isLoadingEditProcesses ? (
                                <p className="text-sm text-gray-500">Carregando processos...</p>
                              ) : !editProcessesList.length ? (
                                <p className="text-sm text-gray-500">Nenhum processo encontrado.</p>
                              ) : (
                                editProcessesList.map((p: any) => {
                                  const checked = editProcessIds.has(p.id);
                                  return (
                                    <label key={p.id} className="flex items-start gap-3 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          setEditProcessIds((prev) => {
                                            const next = new Set(prev);
                                            if (e.target.checked) next.add(p.id);
                                            else next.delete(p.id);
                                            return next;
                                          });
                                        }}
                                        className="mt-1"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          <p className="text-sm text-gray-800">{p.processNumber}</p>
                                          {Array.isArray(p.tags) && p.tags.map((tag: any) => (
                                            <span key={tag.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                              {tag.name}
                                            </span>
                                          ))}
                                        </div>
                                        {Array.isArray(p.parts) && p.parts.length > 0 ? (
                                          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                                            {p.parts.map((part: any) => (
                                              <span key={part.id} className="text-[10px] text-gray-500">
                                                {part.name}{part.pole ? ` (${part.pole})` : ''}
                                              </span>
                                            ))}
                                          </div>
                                        ) : p.parties ? (
                                          <p className="text-xs text-gray-500 truncate">{p.parties}</p>
                                        ) : null}
                                      </div>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          )}

                          <p className="text-[11px] text-gray-500 mb-3">
                            Processos selecionados: {editProcessIds.size}
                          </p>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={saveEdit}
                              disabled={updateInviteMutation.isPending}
                              className="flex-1 px-4 py-2 rounded-[10px] bg-[#06141B] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                            >
                              {updateInviteMutation.isPending ? 'Salvando...' : 'Salvar'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="flex-1 px-4 py-2 rounded-[10px] border border-gray-200 text-gray-700 text-sm hover:bg-gray-50"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}

                      {/* View processes panel */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-100 bg-gray-50/80">
                          {isLoadingInviteProcessesList ? (
                            <p className="text-sm text-gray-500 py-3">Carregando processos...</p>
                          ) : !inviteProcesses.length ? (
                            <p className="text-sm text-gray-500 py-3">Nenhum processo vinculado.</p>
                          ) : (
                            <ul className="space-y-2 py-3">
                              {inviteProcesses.map((p: any) => (
                                <li key={p.id}>
                                  <ProcessCard p={p} />
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {inviteTotalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setInvitePage((p) => Math.max(1, p - 1))}
                    disabled={invitePage === 1}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium bg-[#F9FAFB] text-[#2D3748] border border-gray-200"
                  >
                    &lt;
                  </button>
                  <span className="px-4 py-2 text-xs text-gray-700">
                    Página {invitePage} de {inviteTotalPages} ({allInvites.length}{' '}
                    {allInvites.length === 1 ? 'convite' : 'convites'})
                  </span>
                  <button
                    type="button"
                    onClick={() => setInvitePage((p) => Math.min(inviteTotalPages, p + 1))}
                    disabled={invitePage === inviteTotalPages}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium bg-[#F9FAFB] text-[#2D3748] border border-gray-200"
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
